import React, { Component } from 'react';
import { Message } from '../common/Message';
import { solveAltcha } from '../../altchaSolver';

// In-configurator free sign-up (issue #71 step 2b). Captures contact
// details, Terms acceptance, and an explicit unticked marketing opt-in (UK
// GDPR and PECR), then posts to api/configuration/freeregister. The backend
// creates the customer and a zero-cost subscription in Chargify and the
// website emails the free License Key, which the customer pastes into the
// License Key box above to continue.
//
// The anti-bot proof-of-work is solved silently in the background (it
// takes a fraction of a second), so there is no separate "I'm not a robot"
// step: the customer only ticks the Terms box. If it is not ready by the
// time they submit, the submit waits for it.
export class FreeRegistration extends Component {
    displayName = FreeRegistration.name;

    constructor(props) {
        super(props);

        this.state = {
            firstName: '',
            lastName: '',
            email: '',
            organization: '',
            agreedToTerms: false,
            marketingOptIn: false,
            submitting: false,
            submitted: false,
            // The email already had a subscription: show the check-your-
            // inbox card with a resend button instead of the fresh-key card.
            alreadyRegistered: false,
            // Names of mandatory fields still missing after a Register
            // click, so each one can be highlighted until it is filled.
            missing: [],
            resending: false,
            resendFailed: false,
            resendMessage: '',
            error: ''
        };

        // The solved payload and the in-flight solve promise are kept off
        // React state because they are background plumbing, not UI.
        this._altchaPayload = '';
        this._altchaPromise = null;
        this.rootRef = React.createRef();
    }

    componentDidMount() {
        // Start the anti-bot check straight away so it is ready by submit.
        this.startAltcha();

        // Arriving from the intro splash's "Get a free License Key" route,
        // bring the registration form into view and focus its first field
        // so the customer lands where they intended.
        if (this.props.expanded && this.rootRef.current) {
            // Focus the first field without scrolling; the form sits
            // where the page put it and the view stays where it is.
            var first = this.rootRef.current.querySelector('#regFirstName');
            if (first) {
                first.focus({ preventScroll: true });
            }
        }
    }

    // Kick off a background solve and remember the promise so submit can
    // await it. Each solve yields a fresh, single-use payload.
    startAltcha = () => {
        this._altchaPayload = '';
        this._altchaPromise = solveAltcha()
            .then((payload) => { this._altchaPayload = payload; return payload; });
        // Swallow background rejections; submit surfaces any failure.
        this._altchaPromise.catch(() => { });
        return this._altchaPromise;
    }

    // Resolve to a usable payload, solving or re-solving if needed.
    ensureAltcha = () => {
        if (this._altchaPayload) {
            return Promise.resolve(this._altchaPayload);
        }
        if (this._altchaPromise) {
            // Retry once if the in-flight solve has already failed.
            return this._altchaPromise.catch(() => this.startAltcha());
        }
        return this.startAltcha();
    }

    update = (field) => (event) => {
        this.setState({
            [field]: event.target.value,
            // Filling a highlighted field clears its highlight straight
            // away rather than waiting for the next Register click.
            missing: this.state.missing.filter((m) => m !== field)
        });
    }

    updateCheck = (field) => (event) => {
        this.setState({
            [field]: event.target.checked,
            missing: this.state.missing.filter((m) => m !== field)
        });
    }

    // The mandatory fields that are not yet usable, in display order. Only
    // the marketing opt-in is optional.
    missingFields = () => {
        var missing = [];
        if (this.state.firstName.trim() === '') { missing.push('firstName'); }
        if (this.state.lastName.trim() === '') { missing.push('lastName'); }
        if (this.state.email.indexOf('@') === -1) { missing.push('email'); }
        if (this.state.organization.trim() === '') { missing.push('organization'); }
        if (this.state.agreedToTerms === false) { missing.push('agreedToTerms'); }
        return missing;
    }

    static fieldLabels = {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email address',
        organization: 'Company',
        agreedToTerms: 'Terms'
    };

    submit = (e) => {
        e.preventDefault();

        var missing = this.missingFields();
        if (missing.length > 0) {
            // Name only what is missing, and highlight those fields, so
            // the customer is not re-told about fields they have filled.
            this.setState({
                missing: missing,
                error: missing
                    .map((m) => FreeRegistration.fieldLabels[m])
                    .join(', ') + ' still required.'
            });
            return;
        }

        this.setState({ submitting: true, error: '', missing: [] });

        // Wait for the background anti-bot check, then register.
        this.ensureAltcha()
            .then((payload) => fetch('api/configuration/freeregister', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    firstName: this.state.firstName,
                    lastName: this.state.lastName,
                    email: this.state.email,
                    organization: this.state.organization,
                    agreedToTerms: this.state.agreedToTerms,
                    marketingOptIn: this.state.marketingOptIn,
                    altcha: payload
                })
            }))
            // Some failures (empty BadRequest, an HTML error page) carry no
            // JSON body, so parse defensively and fall back to no body.
            .then((response) => response.text().then((text) => {
                var body = null;
                try { body = text ? JSON.parse(text) : null; } catch (e) { }
                return { ok: response.ok, body: body };
            }))
            .then(({ ok, body }) => {
                if (ok && body && body.success) {
                    // The License Key box must appear now, so the
                    // customer can paste the emailed key. Using the
                    // emailed key is what confirms they control the
                    // address, so the key is never entered for them.
                    if (this.props.onRegistered) {
                        this.props.onRegistered();
                    }
                    this.setState({
                        submitted: true,
                        submitting: false,
                        alreadyRegistered: !!body.alreadyRegistered
                    });
                } else {
                    // The challenge is single-use, so solve a fresh one for
                    // the next attempt.
                    this.startAltcha();
                    this.setState({
                        submitting: false,
                        error: (body && body.message) ||
                            'Registration failed. Please try again.'
                    });
                }
            })
            .catch(() => {
                this.startAltcha();
                this.setState({
                    submitting: false,
                    error: 'Registration failed. Please try again, or ' +
                        'contact support@51degrees.com.'
                });
            });
    }

    // Re-send the existing key email for an already-active address. Each
    // attempt costs a fresh anti-bot solve and shares the registration
    // rate limit, so the button cannot be used to spam an inbox.
    resend = () => {
        this.setState({ resending: true, resendMessage: '' });
        this.ensureAltcha()
            .then((payload) => fetch('api/configuration/resendfreekey', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: this.state.email,
                    altcha: payload
                })
            }))
            .then((response) => response.text().then((text) => {
                var body = null;
                try { body = text ? JSON.parse(text) : null; } catch (e) { }
                return body;
            }))
            .then((body) => {
                this.startAltcha();
                this.setState({
                    resending: false,
                    resendFailed: !(body && body.success),
                    resendMessage: (body && body.message) ||
                        'The email could not be re-sent. Contact ' +
                        'support@51degrees.com.'
                });
            })
            .catch(() => {
                this.startAltcha();
                this.setState({
                    resending: false,
                    resendFailed: true,
                    resendMessage: 'The email could not be re-sent. ' +
                        'Contact support@51degrees.com.'
                });
            });
    }

    render() {
        if (this.state.submitted && this.state.alreadyRegistered) {
            return (
                <article className="c-card c-card--success">
                    <header className="c-card__header">
                        <h3 className="c-card__heading">
                            Already active. Check your inbox!
                        </h3>
                    </header>
                    <p>
                        <strong>{this.state.email}</strong> already has a
                        free License Key. Find the License Key email in
                        your inbox and paste the key into the box above to
                        continue.
                    </p>
                    <button className="b-btn" onClick={this.resend}
                        disabled={this.state.resending}>
                        {this.state.resending
                            ? 'Re-sending…'
                            : 'Resend the License Key email'}
                    </button>
                    {this.state.resendMessage !== '' ? (
                        <Message message={this.state.resendMessage}
                            className={this.state.resendFailed
                                ? 'c-license-key__error' : undefined} />
                    ) : null}
                </article>
            );
        }
        if (this.state.submitted) {
            return (
                <article className="c-card c-card--success">
                    <header className="c-card__header">
                        <h3 className="c-card__heading">
                            Check your email
                        </h3>
                    </header>
                    <p>
                        Your free License Key is on its way to
                        {' '}<strong>{this.state.email}</strong>. Copy the
                        key from that email and paste it into the License
                        Key box above to continue.
                    </p>
                </article>
            );
        }

        // Highlight a mandatory field the last Register click found
        // missing, until it is filled.
        var inputClass = (field) =>
            'b-input b-input--block' +
            (this.state.missing.indexOf(field) !== -1
                ? ' b-input--error'
                : '');

        return (
            <article className="c-card" ref={this.rootRef}>
                {this.props.preselected ? (
                    <p className="c-free-registration__preselected">
                        We have pre-selected all our free properties for
                        you. Sign up for free to get started.
                    </p>
                ) : null}
                <p className="g-info__description">
                    Register to receive a free License Key via email. No
                    card needed. Paste the key into the box above to
                    continue.
                </p>
                <div className="b-form-group">
                    <label className="b-label" htmlFor="regFirstName">First name</label>
                    <input type="text" className={inputClass('firstName')} id="regFirstName"
                        name="firstName" autoComplete="given-name" required
                        value={this.state.firstName} onChange={this.update('firstName')} />
                </div>
                <div className="b-form-group">
                    <label className="b-label" htmlFor="regLastName">Last name</label>
                    <input type="text" className={inputClass('lastName')} id="regLastName"
                        name="lastName" autoComplete="family-name" required
                        value={this.state.lastName} onChange={this.update('lastName')} />
                </div>
                <div className="b-form-group">
                    <label className="b-label" htmlFor="regEmail">Email address</label>
                    <input type="email" className={inputClass('email')} id="regEmail"
                        name="email" autoComplete="email" required
                        placeholder="e.g. john_smith@email.com"
                        value={this.state.email} onChange={this.update('email')} />
                </div>
                <div className="b-form-group">
                    <label className="b-label" htmlFor="regOrganization">Company</label>
                    <input type="text" className={inputClass('organization')} id="regOrganization"
                        name="organization" autoComplete="organization" required
                        value={this.state.organization} onChange={this.update('organization')} />
                </div>
                <span className={'c-select c-select--stacked c-reg-terms' +
                    (this.state.missing.indexOf('agreedToTerms') !== -1
                        ? ' c-select--error' : '')}>
                    <input type="checkbox" className="c-select__input is-selectable"
                        id="regTerms" checked={this.state.agreedToTerms}
                        onChange={this.updateCheck('agreedToTerms')} />
                    <label className="c-select__label" htmlFor="regTerms">
                        <span className="c-select__text">
                            I accept the <a href="https://51degrees.com/terms/free/"
                                target="_blank" rel="noopener noreferrer"
                                className="b-link">Terms and Conditions</a> and
                            confirm I am a person, not an automated system
                        </span>
                    </label>
                </span>
                <span className="c-select c-select--stacked">
                    <input type="checkbox" className="c-select__input is-selectable"
                        id="regMarketing" checked={this.state.marketingOptIn}
                        onChange={this.updateCheck('marketingOptIn')} />
                    <label className="c-select__label" htmlFor="regMarketing">
                        <span className="c-select__text">
                            Email me about new products and offers *
                        </span>
                    </label>
                </span>
                <button className="b-btn" onClick={this.submit}
                    disabled={this.state.submitting}>
                    {this.state.submitting ? 'Registering…' : 'Register'}
                </button>
                {this.state.error !== '' ? (
                    <Message message={this.state.error}
                        className="c-license-key__error" />
                ) : null}
            </article>
        );
    }
}
